/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import prepareSearchQuery from '../../helpers/assets/prepareSearchQuery';
import confirmAndClearOtherSelectionLayer from '../../helpers/confirmAndClearOtherSelectionLayer';
import confirmAndClearOtherProductSelectionSource from '../../helpers/confirmAndClearOtherProductSelectionSource';
import { PRODUCTS_EXPORT_LIMIT } from '../../components/ProductsExportCsvDialog/config';
import { getVariantsBulk } from './getVariantsBulk';
import { materializeSelection } from './helpers/applySelection';
import { shouldLoadProductVariants } from './helpers/productVariantsHelpers';
import { isSelectableItem } from './helpers/selectionKey';
import { getRangeAddEntries } from './helpers/treeSelection';

// Same ES-window-safe sentinel db.products.search accepts (searchSizeAllAssetsEsLimit).
export const SEARCH_SIZE_ALL_ASSETS_ES_LIMIT = 100000000;

function resolveTreeProductVariants(product, variantsByProductId) {
  if (product.variants?.length) return product.variants;
  return variantsByProductId[product._id]
    ?? variantsByProductId[String(product._id)]
    ?? [];
}

async function selectAllFromTree({ dispatch, getState }) {
  const treeProducts = getState().products?.items || [];
  const idsToLoad = treeProducts
    .filter(shouldLoadProductVariants)
    .map((product) => product._id);

  let variantsByProductId = {};
  if (idsToLoad.length) {
    const result = await dispatch(getVariantsBulk({ productIds: idsToLoad }));
    if (getVariantsBulk.fulfilled.match(result)) {
      variantsByProductId = result.payload || {};
    }
  }

  const products = getState().products?.items || treeProducts;
  const rangeNodes = products.map((product) => ({
    nodeId: product._id,
    isVariant: false,
    productId: product._id,
    node: {
      ...product,
      variants: resolveTreeProductVariants(product, variantsByProductId),
    },
  }));

  return getRangeAddEntries(rangeNodes);
}

export const selectAllProducts = createAsyncThunk(
  'products/selectAllProducts',
  async (_, { dispatch, extra: { sdk, Logger }, getState }) => {
    try {
      const canProceed = await confirmAndClearOtherSelectionLayer({
        dispatch,
        getState,
        targetLayer: 'products',
      });
      if (!canProceed) return { cancelled: true, overLimit: false, items: [] };

      const isTreeSelectAll = getState().products?.ui?.selection?.source === 'tree';
      if (!isTreeSelectAll) {
        const canSwitchSource = await confirmAndClearOtherProductSelectionSource({
          dispatch,
          getState,
          targetSource: 'search',
        });
        if (!canSwitchSource) return { cancelled: true, overLimit: false, items: [] };
      }

      if (isTreeSelectAll) {
        const items = await selectAllFromTree({ dispatch, getState });
        if (items.length > PRODUCTS_EXPORT_LIMIT) {
          return { overLimit: true, items: [] };
        }
        return {
          overLimit: false,
          items,
          isComplete: true,
          source: 'tree',
        };
      }

      const query = prepareSearchQuery();
      const { data } = await sdk.products.search({
        ...query,
        onlyIds: true,
        size: SEARCH_SIZE_ALL_ASSETS_ES_LIMIT,
      });

      // onlyIds may omit `type` (ES `_source` often does). The checkbox key is `${type}:${_id}`,
      // so fill type from the already-loaded grid. Untyped leftovers are unknown — do not
      // default untyped to master on the Search path.
      const loadedById = new Map(
        (getState().products?.search?.items || []).map((item) => [String(item._id), item]),
      );
      const mapped = (data.products || []).map((product) => {
        const loaded = loadedById.get(String(product._id));
        const type = product.type || loaded?.type;
        const entry = {
          _id: String(product._id),
          type,
        };
        const baseProductId = product.baseProductId || loaded?.baseProductId;
        const masterProductId = product.masterProductId || loaded?.masterProductId;
        const listingStatus = product.listingStatus || loaded?.listingStatus;
        const channelId = product.channelId || loaded?.channelId || loaded?.salesChannelId;

        if (baseProductId) entry.baseProductId = baseProductId;
        if (masterProductId) entry.masterProductId = masterProductId;
        if (listingStatus) entry.listingStatus = listingStatus;
        if (channelId) entry.channelId = channelId;
        return entry;
      });
      const items = mapped.filter(isSelectableItem);
      const hasUntyped = mapped.some((product) => !product.type);
      if (items.length > PRODUCTS_EXPORT_LIMIT) {
        return { overLimit: true, items: [] };
      }

      return {
        overLimit: false,
        items,
        isComplete: !hasUntyped,
        source: 'search',
      };
    } catch (error) {
      Logger.error(new Error('Error selecting all products'), { error }, [
        'ProductsSelectAllFailed',
        { errorMessage: error?.message || 'NoMessage' },
      ]);
      throw error;
    }
  },
  {
    condition: (_, { getState }) => !getState().products?.ui?.isSelectingAll,
  },
);

function isCurrentSelectAllRequest(state, requestId) {
  return state.ui.selectAllRequestId === requestId;
}

function settleSelectAll(state) {
  state.ui.isSelectingAll = false;
  state.ui.selectAllRequestId = null;
}

export const reducer = (builder) => {
  builder
    .addCase(selectAllProducts.pending, (state, { meta }) => {
      state.ui.isSelectingAll = true;
      state.ui.selectAllRequestId = meta.requestId;
    })
    .addCase(selectAllProducts.fulfilled, (state, { payload, meta }) => {
      if (!isCurrentSelectAllRequest(state, meta.requestId)) return;
      settleSelectAll(state);
      if (payload.cancelled || payload.overLimit || !payload.items?.length) return;
      state.ui.selection = materializeSelection(payload.items, {
        isComplete: payload.isComplete !== false,
        source: payload.source,
      });
    })
    .addCase(selectAllProducts.rejected, (state, { meta }) => {
      if (!isCurrentSelectAllRequest(state, meta.requestId)) return;
      settleSelectAll(state);
    });
};
